/**
 * Multi-select chip picker with free-text fallback.
 *
 * Used for languages + accents on the actor profile. The supplied
 * `options` are the suggested chips (rendered with their localized
 * labels); the user can also add free-text values which become first-class
 * chips. Selected values are returned as an array of the underlying
 * codes (e.g. ['en', 'es']).
 *
 * Why home-built (no @ohear/ui Combobox): the requirements are narrow
 * (chips + add) and a custom input keeps keyboard handling explicit
 * (Enter to add, Backspace on empty input to remove last).
 */
import { type KeyboardEvent, useState } from 'react'

export interface ChipOption {
  /** Underlying code stored in the array (e.g. 'en', 'rp'). */
  value: string
  /** Localized label shown in the chip suggestions row. */
  label: string
}

export interface ChipMultiSelectProps {
  value: readonly string[]
  onChange: (next: string[]) => void
  /** Suggested chips shown above the input. Click adds. */
  options?: readonly ChipOption[]
  /** Placeholder for the free-text input. */
  placeholder?: string
  disabled?: boolean
  /** Cap the number of selections (defaults to 40). */
  maxItems?: number
}

export function ChipMultiSelect(props: ChipMultiSelectProps) {
  const {
    value,
    onChange,
    options = [],
    placeholder = 'Type and press Enter…',
    disabled,
    maxItems = 40,
  } = props
  const [draft, setDraft] = useState('')

  const add = (raw: string) => {
    const v = raw.trim().toLowerCase().replace(/\s+/g, '_')
    if (!v) return
    if (value.includes(v)) return
    if (value.length >= maxItems) return
    onChange([...value, v])
    setDraft('')
  }

  const remove = (v: string) => {
    onChange(value.filter((x) => x !== v))
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      add(draft)
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      // Remove the last chip when the input is empty.
      onChange(value.slice(0, -1))
    }
  }

  const labelFor = (v: string): string => {
    const opt = options.find((o) => o.value === v)
    return opt?.label ?? v
  }

  // Filter options to those not yet selected, capped at 12 visible.
  const remainingOptions = options.filter((o) => !value.includes(o.value)).slice(0, 12)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {value.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2,#eef2f7)] px-2 py-0.5 text-[12px] text-[var(--text)]"
          >
            {labelFor(v)}
            <button
              type="button"
              onClick={() => remove(v)}
              disabled={disabled}
              aria-label={`Remove ${labelFor(v)}`}
              className="ml-1 text-[var(--text-3)] hover:text-[var(--text)] bg-transparent border-0 p-0 cursor-pointer"
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => draft && add(draft)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled || value.length >= maxItems}
          className="flex-1 min-w-[120px] border-0 outline-none bg-transparent text-[13px]"
        />
      </div>
      {remainingOptions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {remainingOptions.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => add(o.value)}
              disabled={disabled}
              className="rounded-full border border-[var(--border)] bg-transparent px-2 py-0.5 text-[12px] text-[var(--text-2)] hover:bg-[var(--surface-2,#eef2f7)] cursor-pointer"
            >
              + {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
